import React, { useEffect, useRef, useState } from 'react';
import { createTask, fetchDeals, fetchLeads, fetchUsersList } from '../../services/api';
import { User } from '../../types';

type EntityType = 'leads' | 'deals';

interface EntityOption {
  id: number;
  name: string;         // leads: name, deals: title (both are display labels)
  subtitle?: string;    // leads: phone/mobile, deals: stage
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
  defaultAssignedTo?: number | null;
}

// Backend TaskController requires a polymorphic parent (lead/deal/customer/
// contact). The two most common on this page are Lead + Deal; keeping the
// picker to those two matches what TaskRow actually renders. Add more only
// when a user asks — chaining Customer/Contact in without demand just
// clutters the UI.
const ENTITY_LABELS: Record<EntityType, string> = { leads: 'Lead', deals: 'Deal' };

const AddTaskModal: React.FC<Props> = ({ isOpen, onClose, onCreated, defaultAssignedTo }) => {
  const [entityType, setEntityType] = useState<EntityType>('leads');
  const [search, setSearch]         = useState('');
  const [results, setResults]       = useState<EntityOption[]>([]);
  const [searching, setSearching]   = useState(false);
  const [selected, setSelected]     = useState<EntityOption | null>(null);

  const [title, setTitle]       = useState('');
  const [dueDate, setDueDate]   = useState('');
  const [dueTime, setDueTime]   = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [users, setUsers]       = useState<User[]>([]);
  const [assignedTo, setAssign] = useState<number | ''>('');

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  // Debounced entity search — 250ms is low enough that typing feels live,
  // high enough that holding a key doesn't fan out one request per char.
  const timer = useRef<number | null>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (timer.current) window.clearTimeout(timer.current);
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    timer.current = window.setTimeout(async () => {
      try {
        const raw = entityType === 'leads'
          ? await fetchLeads({ search: q })
          : await fetchDeals({ search: q });
        // Both endpoints paginate → {data: []}; some wrapped responses are
        // plain arrays. Normalise before mapping so we don't crash on edge.
        const list: any[] = Array.isArray(raw) ? raw : (raw?.data ?? []);
        setResults(list.slice(0, 10).map(r => ({
          id: r.id,
          name: entityType === 'leads'
            ? (r.name ?? `Lead #${r.id}`)
            : (r.title ?? r.name ?? `Deal #${r.id}`),
          subtitle: entityType === 'leads'
            ? (r.mobile || r.phone || '')
            : (r.stage || r.status || ''),
        })));
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [search, entityType, isOpen]);

  // Users for the assignee dropdown — loaded once per open.
  useEffect(() => {
    if (!isOpen) return;
    fetchUsersList().then((u: User[]) => setUsers(Array.isArray(u) ? u : [])).catch(() => setUsers([]));
  }, [isOpen]);

  // Reset state each time the modal opens so a prior aborted draft doesn't
  // leak into the next session.
  useEffect(() => {
    if (!isOpen) return;
    setEntityType('leads');
    setSearch('');
    setResults([]);
    setSelected(null);
    setTitle('');
    setDueDate('');
    setDueTime('');
    setPriority('medium');
    setAssign(defaultAssignedTo ?? '');
    setError(null);
  }, [isOpen, defaultAssignedTo]);

  // Esc closes. Keep Enter submission local to the title field only, so
  // Enter in the search box doesn't accidentally send an incomplete form.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const canSubmit = !!selected && title.trim().length > 0 && !saving;

  const submit = async () => {
    if (!canSubmit || !selected) return;
    setSaving(true);
    setError(null);
    try {
      await createTask(entityType, selected.id, {
        title: title.trim(),
        due_date: dueDate || undefined,
        due_time: dueTime || undefined,
        priority,
        assigned_to: assignedTo || undefined,
      });
      onCreated();
      onClose();
    } catch (e: any) {
      // Prefer the server's validation message when present; fall back to a
      // generic retry hint so the user is never left staring at silence.
      const msg = e?.response?.data?.message
        || Object.values(e?.response?.data?.errors ?? {})[0]?.toString()
        || 'Failed to add task. Please try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={header}>
          <h3 style={{ margin: 0, fontSize: 16, color: 'var(--navy)', fontWeight: 700 }}>Add New Task</h3>
          <button style={closeBtn} onClick={onClose} aria-label="Close">×</button>
        </div>

        <div style={body}>
          <div style={field}>
            <label style={labelStyle}>Link to</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {(Object.keys(ENTITY_LABELS) as EntityType[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => { setEntityType(t); setSelected(null); setResults([]); setSearch(''); }}
                  style={pill(entityType === t)}
                >
                  {ENTITY_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div style={field}>
            <label style={labelStyle}>
              {selected
                ? `Selected ${ENTITY_LABELS[entityType]}`
                : `Search ${entityType}`}
            </label>
            {selected ? (
              <div style={selectedChip}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--navy)' }}>{selected.name}</div>
                  {selected.subtitle && (
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{selected.subtitle}</div>
                  )}
                </div>
                <button type="button" onClick={() => setSelected(null)} style={clearBtn}>Change</button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={`Type at least 2 characters to search ${entityType}…`}
                  style={input}
                  autoFocus
                />
                {search.trim().length >= 2 && (
                  <div style={resultsBox}>
                    {searching && <div style={resultEmpty}>Searching…</div>}
                    {!searching && results.length === 0 && (
                      <div style={resultEmpty}>No matches. Try a different term.</div>
                    )}
                    {!searching && results.map(r => (
                      <button key={r.id} type="button" onClick={() => setSelected(r)} style={resultRow}>
                        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--navy)' }}>{r.name}</div>
                        {r.subtitle && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{r.subtitle}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div style={field}>
            <label style={labelStyle}>Task Title <span style={{ color: 'var(--coral)' }}>*</span></label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && canSubmit) submit(); }}
              placeholder="e.g., Call back about pricing"
              style={input}
            />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ ...field, flex: 1 }}>
              <label style={labelStyle}>Due Date</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={input} />
            </div>
            <div style={{ ...field, width: 120 }}>
              <label style={labelStyle}>Time</label>
              <input type="time" value={dueTime} onChange={e => setDueTime(e.target.value)} style={input} />
            </div>
            <div style={{ ...field, width: 140 }}>
              <label style={labelStyle}>Priority</label>
              <select
                value={priority}
                onChange={e => setPriority(e.target.value as 'low' | 'medium' | 'high')}
                style={input}
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div style={field}>
            <label style={labelStyle}>Assign to (optional)</label>
            <select
              value={assignedTo}
              onChange={e => setAssign(e.target.value ? Number(e.target.value) : '')}
              style={input}
            >
              <option value="">— Any agent —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>

          {error && <div style={errorBox}>{error}</div>}
        </div>

        <div style={footer}>
          <button type="button" onClick={onClose} style={btnSecondary}>Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            style={btnPrimary(canSubmit)}
          >
            {saving ? 'Saving…' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 20,
};
const modal: React.CSSProperties = {
  background: '#fff', borderRadius: 12, width: 'min(520px, 100%)',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
const header: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '16px 20px', borderBottom: '1px solid var(--border)',
};
const body: React.CSSProperties = { padding: '16px 20px', overflow: 'auto', flex: 1 };
const footer: React.CSSProperties = {
  display: 'flex', gap: 10, justifyContent: 'flex-end',
  padding: '12px 20px', borderTop: '1px solid var(--border)',
};
const field: React.CSSProperties = { marginBottom: 14 };
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600,
  color: 'var(--text-2)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3,
};
const input: React.CSSProperties = {
  width: '100%', padding: '8px 12px', fontSize: 14,
  border: '1px solid var(--border)', borderRadius: 8, background: '#fff',
  color: 'var(--navy)', outline: 'none', boxSizing: 'border-box',
};
const pill = (active: boolean): React.CSSProperties => ({
  padding: '6px 16px', fontSize: 13, fontWeight: 600,
  border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
  background: active ? 'var(--primary)' : '#fff',
  color: active ? '#fff' : 'var(--text-2)',
  borderRadius: 999, cursor: 'pointer',
});
const resultsBox: React.CSSProperties = {
  marginTop: 6, border: '1px solid var(--border)', borderRadius: 8,
  maxHeight: 180, overflow: 'auto', background: '#fff',
};
const resultRow: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: '10px 12px', border: 'none', borderBottom: '1px solid var(--border)',
  background: '#fff', cursor: 'pointer',
};
const resultEmpty: React.CSSProperties = {
  padding: '12px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center',
};
const selectedChip: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 14px', border: '1px solid var(--primary)',
  borderRadius: 8, background: 'rgba(83,74,183,0.06)',
};
const clearBtn: React.CSSProperties = {
  background: 'none', border: '1px solid var(--border)', borderRadius: 6,
  padding: '4px 10px', fontSize: 12, color: 'var(--text-2)', cursor: 'pointer',
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: 26, lineHeight: 1,
  cursor: 'pointer', color: 'var(--text-muted)',
};
const btnSecondary: React.CSSProperties = {
  padding: '8px 18px', fontSize: 14, fontWeight: 600,
  border: '1px solid var(--border)', background: '#fff', color: 'var(--text-2)',
  borderRadius: 8, cursor: 'pointer',
};
const btnPrimary = (enabled: boolean): React.CSSProperties => ({
  padding: '8px 20px', fontSize: 14, fontWeight: 600,
  border: 'none', borderRadius: 8,
  background: enabled ? 'var(--primary)' : 'var(--border)',
  color: '#fff', cursor: enabled ? 'pointer' : 'not-allowed',
  minWidth: 120,
});
const errorBox: React.CSSProperties = {
  padding: '10px 12px', background: 'rgba(239,68,68,0.08)',
  color: 'var(--coral)', border: '1px solid rgba(239,68,68,0.25)',
  borderRadius: 8, fontSize: 13, marginTop: 4,
};

export default AddTaskModal;
